import { BarChart3, PieChart as PieChartIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DashboardKpis } from "../../../api/dashboard";
import { formatMinor } from "../../../lib/money";

interface AnalyticsChartSuiteProps {
  kpis: DashboardKpis | null;
  isLoading: boolean;
}

const CHART_NAVY = "#002d72";
const CHART_SLATE = "#475569";
const CHART_STEEL = "#64748b";

const INVENTORY_COLORS = [CHART_NAVY, CHART_SLATE, CHART_STEEL];

interface SalesChartPoint {
  month: string;
  totalMinor: number;
  displayAmount: string;
}

interface InventoryChartPoint {
  name: string;
  itemType: number;
  valueMinor: number;
  itemCount: number;
  displayValue: string;
}

function ChartTooltipValue({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-medium text-slate-600">{label}</p>
      <p className="mt-1 font-semibold text-godo-navy" dir="ltr">
        {value}
      </p>
    </div>
  );
}

function ChartSkeleton({ title }: { title: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <div className="mt-6 h-64 animate-pulse rounded-xl bg-slate-100" />
    </div>
  );
}

export function AnalyticsChartSuite({ kpis, isLoading }: AnalyticsChartSuiteProps) {
  const { t } = useTranslation();

  const salesData = useMemo<SalesChartPoint[]>(() => {
    if (!kpis) {
      return [];
    }
    return kpis.monthlySalesTrend.map((point) => ({
      month: point.month,
      totalMinor: point.totalMinor,
      displayAmount: formatMinor(point.totalMinor),
    }));
  }, [kpis]);

  const inventoryData = useMemo<InventoryChartPoint[]>(() => {
    if (!kpis) {
      return [];
    }

    const labelForItemType = (itemType: number): string => {
      switch (itemType) {
        case 0:
          return t("inventory.item_type_storable");
        case 1:
          return t("inventory.item_type_service");
        case 2:
          return t("inventory.item_type_consumable");
        default:
          return t("inventory.item_type_unknown");
      }
    };

    return kpis.inventoryDistribution.map((bucket) => ({
      name: labelForItemType(bucket.itemType),
      itemType: bucket.itemType,
      valueMinor: bucket.totalValueMinor,
      itemCount: bucket.itemCount,
      displayValue: formatMinor(bucket.totalValueMinor),
    }));
  }, [kpis, t]);

  const inventoryTotalMinor = useMemo(
    () => inventoryData.reduce((sum, row) => sum + row.valueMinor, 0),
    [inventoryData],
  );

  if (isLoading) {
    return (
      <section className="grid gap-4 xl:grid-cols-2">
        <ChartSkeleton title={t("dashboard.sales_trend")} />
        <ChartSkeleton title={t("dashboard.inventory_distribution")} />
      </section>
    );
  }

  if (!kpis) {
    return (
      <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
        {t("dashboard.kpis_unavailable")}
      </section>
    );
  }

  return (
    <section className="grid gap-4 xl:grid-cols-2">
      <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-godo-navy" aria-hidden="true" />
              <h2 className="text-lg font-semibold text-slate-900">
                {t("dashboard.sales_trend")}
              </h2>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {t("dashboard.sales_trend_hint")}
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-end">
            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t("dashboard.period_total")}
            </p>
            <p className="text-sm font-bold text-godo-navy" dir="ltr">
              {formatMinor(kpis.totalSalesMinor)}
            </p>
          </div>
        </div>

        <div className="h-72 w-full" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={salesData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="month"
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip
                cursor={{ fill: "rgba(0, 45, 114, 0.06)" }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) {
                    return null;
                  }
                  const row = payload[0]?.payload as SalesChartPoint | undefined;
                  return (
                    <ChartTooltipValue
                      label={String(label ?? "")}
                      value={row?.displayAmount ?? formatMinor(0)}
                    />
                  );
                }}
              />
              <Bar
                dataKey="totalMinor"
                fill={CHART_NAVY}
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>

      <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-godo-navy" aria-hidden="true" />
              <h2 className="text-lg font-semibold text-slate-900">
                {t("dashboard.inventory_distribution")}
              </h2>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {t("dashboard.inventory_distribution_hint")}
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-end">
            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t("dashboard.inventory_total_value")}
            </p>
            <p className="text-sm font-bold text-godo-navy" dir="ltr">
              {formatMinor(inventoryTotalMinor)}
            </p>
          </div>
        </div>

        <div className="h-72 w-full" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={inventoryData}
                dataKey="valueMinor"
                nameKey="name"
                innerRadius={62}
                outerRadius={96}
                paddingAngle={2}
                stroke="#ffffff"
                strokeWidth={2}
              >
                {inventoryData.map((entry, index) => (
                  <Cell
                    key={`slice-${entry.itemType}`}
                    fill={INVENTORY_COLORS[index % INVENTORY_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) {
                    return null;
                  }
                  const row = payload[0]?.payload as InventoryChartPoint | undefined;
                  if (!row) {
                    return null;
                  }
                  return (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
                      <p className="font-medium text-slate-600">{row.name}</p>
                      <p className="mt-1 font-semibold text-godo-navy" dir="ltr">
                        {row.displayValue}
                      </p>
                      <p className="mt-1 text-slate-500">
                        {t("dashboard.inventory_item_count", { count: row.itemCount })}
                      </p>
                    </div>
                  );
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                formatter={(value) => (
                  <span className="text-xs text-slate-600">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {inventoryData.map((row, index) => (
            <div
              key={`legend-${row.itemType}`}
              className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: INVENTORY_COLORS[index % INVENTORY_COLORS.length] }}
                  aria-hidden="true"
                />
                <p className="truncate text-xs font-medium text-slate-700">{row.name}</p>
              </div>
              <p className="mt-1 text-sm font-bold text-godo-navy" dir="ltr">
                {row.displayValue}
              </p>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
}
